import React, { useState } from "react";

let useNavigate;
try { useNavigate = require("react-router-dom").useNavigate; }
catch { useNavigate = () => (path) => { window.location.href = path; }; }

let ameonLogo = null;
try { ameonLogo = require("../ameon.jpg"); } catch {}
const LOGO_SRC = ameonLogo || "/logo192.png";

// ── Change this to your server URL ──
const API_BASE = process.env.REACT_APP_API_URL || "https://canada-billing.ameontech.com";

const GlobalStyles = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@600;700;800&family=DM+Sans:wght@300;400;500;600&display=swap');

    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
    html { scroll-behavior: smooth; -webkit-font-smoothing: antialiased; }
    body {
      font-family: 'DM Sans', system-ui, sans-serif;
      color: #1a1a2e;
      background: linear-gradient(135deg, #f0f7ff 0%, #fff5f4 50%, #f5f3ff 100%);
      background-attachment: fixed;
      font-size: 15px;
      line-height: 1.6;
    }
    button { font-family: inherit; cursor: pointer; }
    input { font-family: inherit; font-size: 15px; }
    a { text-decoration: none; }

    :root {
      --blue: #4f8ef7;
      --blue-dark: #3a7ae8;
      --blue-light: #eff6ff;
      --blue-mid: #bfdbfe;
      --ink: #1a1a2e;
      --ink-muted: #555;
      --ink-faint: #888;
      --surface: #ffffff;
      --border: #f0f0f0;
      --border-strong: #e0e0e0;
      --green: #52c97e;
      --coral: #f97b6b;
      --radius-sm: 8px;
      --radius-md: 12px;
      --radius-lg: 20px;
      --radius-xl: 28px;
      --text-xs: 12px;
      --text-sm: 13px;
      --text-base: 15px;
      --text-md: 16px;
      --text-lg: 18px;
      --text-xl: 20px;
    }

    @keyframes fadeUp {
      from { opacity: 0; transform: translateY(28px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    @keyframes spin {
      to { transform: rotate(360deg); }
    }
    @keyframes pulseDot {
      0%, 100% { box-shadow: 0 0 0 0 rgba(82,201,126,0.5); }
      50%       { box-shadow: 0 0 0 8px rgba(82,201,126,0); }
    }
    @keyframes float {
      0%, 100% { transform: translateY(0px); }
      50%       { transform: translateY(-8px); }
    }

    .login-card { animation: fadeUp 0.65s cubic-bezier(.22,.9,.36,1) both; }
    .login-side  { animation: fadeUp 0.65s cubic-bezier(.22,.9,.36,1) 0.1s both; }

    .login-input {
      width: 100%;
      padding: 13px 16px;
      border: 1.5px solid var(--border-strong);
      border-radius: var(--radius-md);
      font-family: 'DM Sans', sans-serif;
      font-size: var(--text-base);
      color: var(--ink);
      background: white;
      transition: border-color 0.2s, box-shadow 0.2s;
      outline: none;
    }
    .login-input:focus {
      border-color: var(--blue);
      box-shadow: 0 0 0 3px rgba(79,142,247,.12);
    }
    .login-input::placeholder { color: var(--ink-faint); }

    .btn-login {
      width: 100%;
      padding: 14px;
      border-radius: 50px;
      border: none;
      background: linear-gradient(135deg, var(--blue), #6366f1);
      color: #fff;
      font-family: 'DM Sans', sans-serif;
      font-size: var(--text-base);
      font-weight: 600;
      cursor: pointer;
      transition: transform 0.2s, box-shadow 0.2s, opacity 0.2s;
      box-shadow: 0 4px 20px rgba(79,142,247,0.35);
      display: flex; align-items: center; justify-content: center; gap: 8px;
    }
    .btn-login:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 8px 30px rgba(79,142,247,0.45); }
    .btn-login:disabled { opacity: 0.6; cursor: not-allowed; transform: none; }

    .floating-card { animation: float 5s ease-in-out infinite; }

    .pulse-dot {
      width: 8px; height: 8px; background: var(--green);
      border-radius: 50%; display: inline-block;
      animation: pulseDot 2s infinite;
    }

    @media (max-width: 768px) {
      .login-side-panel { display: none !important; }
      .login-main { padding: 24px 20px !important; }
    }
  `}</style>
);

export default function Login() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [showPass, setShowPass] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    if (error) setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.password) {
      setError("Please enter your email and password.");
      return;
    }
    setIsLoading(true);
    setError("");
    try {
      const res = await fetch(`${API_BASE}/api/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: form.email, password: form.password }),
      });

      const data = await res.json();

      if (!res.ok) {
        // Server returned an error (401, 400, etc.)
        setError(data.message || "Login failed. Please check your credentials.");
        return;
      }

      // Save token and user info to localStorage
      localStorage.setItem("token", data.token);
      localStorage.setItem("user", JSON.stringify(data.user));
      localStorage.setItem("tenantId", data.tenantId);

      // Redirect to dashboard
      navigate("/dashboard");
    } catch (err) {
      // Network error or server is down
      setError("Could not connect to server. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div style={{ minHeight: "100vh", display: "flex", background: "linear-gradient(135deg, #f0f7ff 0%, #fff5f4 50%, #f5f3ff 100%)" }}>
      <GlobalStyles />

      {/* ── LEFT SIDE PANEL ── */}
      <div className="login-side-panel" style={{
        flex: 1, background: "linear-gradient(160deg, #1a1a2e 0%, #16213e 60%, #0d3b6e 100%)",
        display: "flex", flexDirection: "column", justifyContent: "space-between",
        padding: "48px 52px", position: "relative", overflow: "hidden",
        minWidth: 420,
      }}>
        {/* Background blobs */}
        <div style={{ position: "absolute", width: 400, height: 400, borderRadius: "50%", background: "#4f8ef7", filter: "blur(100px)", opacity: 0.12, top: -80, right: -80, pointerEvents: "none" }} />
        <div style={{ position: "absolute", width: 300, height: 300, borderRadius: "50%", background: "#f97b6b", filter: "blur(80px)", opacity: 0.1, bottom: -60, left: -60, pointerEvents: "none" }} />

        {/* Logo */}
        <div className="login-side" style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", position: "relative", zIndex: 2 }}
          onClick={() => navigate("/")}>
          <img src={LOGO_SRC} alt="InvoiceCloud" style={{ width: 36, height: 36, borderRadius: 10, objectFit: "contain", border: "1px solid rgba(255,255,255,0.1)" }} onError={e => e.currentTarget.style.display = "none"} />
          <div>
            <div style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700, fontSize: 22, color: "white", lineHeight: 1 }}>
              Invoice<span style={{ color: "#4f8ef7" }}>Cloud</span>
            </div>
            <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, fontWeight: 600, letterSpacing: "1.4px", color: "#4f8ef7", textTransform: "uppercase" }}>Canada</div>
          </div>
        </div>

        {/* Main tagline */}
        <div className="login-side" style={{ position: "relative", zIndex: 2 }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)", padding: "6px 14px", borderRadius: 50, marginBottom: 24 }}>
            <span className="pulse-dot" />
            <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 12, color: "rgba(255,255,255,0.7)" }}>GST/HST billing platform</span>
          </div>
          <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(28px,3.5vw,40px)", fontWeight: 800, color: "white", lineHeight: 1.15, letterSpacing: "-0.5px", marginBottom: 16 }}>
            Your Canadian billing dashboard awaits
          </h2>
          <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 15, color: "rgba(255,255,255,0.55)", lineHeight: 1.75, maxWidth: 340 }}>
            Professional GST/HST/PST invoicing, payroll management, and CRA-compliant reporting — all in one place.
          </p>
        </div>

        {/* Floating dashboard card */}
        <div className="floating-card login-side" style={{ position: "relative", zIndex: 2 }}>
          <div style={{ background: "rgba(255,255,255,0.07)", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 20, padding: "22px 24px", backdropFilter: "blur(12px)" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18 }}>
              <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 13, color: "rgba(255,255,255,0.6)", fontWeight: 500 }}>This Month</span>
              <span style={{ background: "rgba(82,201,126,0.15)", color: "#52c97e", padding: "3px 10px", borderRadius: 20, fontSize: 11, fontFamily: "'DM Sans', sans-serif", fontWeight: 600 }}>+12.4%</span>
            </div>
            {[
              { label: "Invoiced",   value: "$42,800", color: "#4f8ef7", pct: 68 },
              { label: "GST/HST",    value: "$5,564",  color: "#f97b6b", pct: 45 },
              { label: "Payroll",    value: "$31,200", color: "#52c97e", pct: 80 },
            ].map(({ label, value, color, pct }) => (
              <div key={label} style={{ marginBottom: 14 }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5 }}>
                  <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 12, color: "rgba(255,255,255,0.5)" }}>{label}</span>
                  <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 12, fontWeight: 600, color: "white" }}>{value}</span>
                </div>
                <div style={{ background: "rgba(255,255,255,0.08)", borderRadius: 6, height: 5 }}>
                  <div style={{ width: `${pct}%`, height: "100%", background: color, borderRadius: 6, transition: "width 1s ease" }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── RIGHT LOGIN FORM ── */}
      <div className="login-main" style={{
        flex: 1, display: "flex", alignItems: "center", justifyContent: "center",
        padding: "48px 40px", position: "relative",
      }}>
        {/* Background blobs */}
        <div style={{ position: "absolute", width: 350, height: 350, borderRadius: "50%", background: "#4f8ef7", filter: "blur(100px)", opacity: 0.06, top: -60, right: -60, pointerEvents: "none" }} />
        <div style={{ position: "absolute", width: 280, height: 280, borderRadius: "50%", background: "#a78bfa", filter: "blur(80px)", opacity: 0.07, bottom: -40, left: -40, pointerEvents: "none" }} />

        <div className="login-card" style={{ width: "100%", maxWidth: 420, position: "relative", zIndex: 2 }}>

          {/* Mobile logo */}
          <div style={{ display: "none", alignItems: "center", gap: 10, marginBottom: 32, cursor: "pointer" }}
            className="mobile-logo" onClick={() => navigate("/")}>
            <img src={LOGO_SRC} alt="InvoiceCloud" style={{ width: 32, height: 32, borderRadius: 8, objectFit: "contain" }} onError={e => e.currentTarget.style.display = "none"} />
            <div style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700, fontSize: 20, color: "var(--ink)" }}>
              Invoice<span style={{ color: "var(--blue)" }}>Cloud</span>
            </div>
          </div>

          <div style={{ marginBottom: 36 }}>
            <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(26px,3vw,34px)", fontWeight: 800, color: "var(--ink)", letterSpacing: "-0.5px", marginBottom: 8, lineHeight: 1.15 }}>
              Welcome back
            </h1>
            <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: "var(--text-base)", color: "var(--ink-muted)" }}>
              Sign in to your InvoiceCloud account
            </p>
          </div>

          {/* Error message */}
          {error && (
            <div style={{
              background: "#fef2f2", border: "1px solid #fecaca",
              borderRadius: "var(--radius-md)", padding: "12px 16px",
              marginBottom: 20, display: "flex", alignItems: "center", gap: 10,
              fontFamily: "'DM Sans', sans-serif", fontSize: "var(--text-sm)", color: "#991b1b",
            }}>
              <span>❌</span> {error}
            </div>
          )}

          <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 16 }}>

            {/* Email */}
            <div>
              <label style={{ display: "block", fontFamily: "'DM Sans', sans-serif", fontSize: "var(--text-sm)", fontWeight: 600, color: "var(--ink)", marginBottom: 7 }}>
                Email Address
              </label>
              <input
                className="login-input"
                type="email"
                name="email"
                placeholder="you@company.ca"
                value={form.email}
                onChange={handleChange}
                autoComplete="email"
                disabled={isLoading}
              />
            </div>

            {/* Password */}
            <div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 7 }}>
                <label style={{ fontFamily: "'DM Sans', sans-serif", fontSize: "var(--text-sm)", fontWeight: 600, color: "var(--ink)" }}>
                  Password
                </label>
                <button type="button"
                  onClick={() => navigate("/forgot-password")}
                  style={{ background: "none", border: "none", color: "var(--blue)", fontFamily: "'DM Sans', sans-serif", fontSize: "var(--text-xs)", fontWeight: 600, cursor: "pointer", padding: 0 }}>
                  Forgot password?
                </button>
              </div>
              <div style={{ position: "relative" }}>
                <input
                  className="login-input"
                  type={showPass ? "text" : "password"}
                  name="password"
                  placeholder="Enter your password"
                  value={form.password}
                  onChange={handleChange}
                  autoComplete="current-password"
                  disabled={isLoading}
                  style={{ paddingRight: 48 }}
                />
                <button type="button"
                  onClick={() => setShowPass(!showPass)}
                  style={{
                    position: "absolute", right: 14, top: "50%", transform: "translateY(-50%)",
                    background: "none", border: "none", cursor: "pointer",
                    color: "var(--ink-faint)", fontSize: 18, lineHeight: 1, padding: 0,
                  }}>
                  {showPass ? "🙈" : "👁️"}
                </button>
              </div>
            </div>

            {/* Submit */}
            <div style={{ marginTop: 4 }}>
              <button type="submit" className="btn-login" disabled={isLoading}>
                {isLoading
                  ? <><span style={{ width: 16, height: 16, border: "2px solid rgba(255,255,255,.4)", borderTopColor: "#fff", borderRadius: "50%", display: "inline-block", animation: "spin .7s linear infinite" }} /> Signing in...</>
                  : "Sign in →"
                }
              </button>
            </div>
          </form>

          {/* Divider */}
          <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "24px 0" }}>
            <div style={{ flex: 1, height: 1, background: "var(--border-strong)" }} />
            <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: "var(--text-xs)", color: "var(--ink-faint)", fontWeight: 500 }}>OR</span>
            <div style={{ flex: 1, height: 1, background: "var(--border-strong)" }} />
          </div>

          {/* Sign up link */}
          <p style={{ textAlign: "center", fontFamily: "'DM Sans', sans-serif", fontSize: "var(--text-sm)", color: "var(--ink-muted)" }}>
            Don't have an account?{" "}
            <button type="button"
              onClick={() => navigate("/signup")}
              style={{ background: "none", border: "none", color: "var(--blue)", fontWeight: 600, cursor: "pointer", fontSize: "var(--text-sm)", padding: 0, fontFamily: "'DM Sans', sans-serif" }}>
              Start free trial →
            </button>
          </p>

          {/* Trust badges */}
          <div style={{ display: "flex", gap: 16, justifyContent: "center", marginTop: 32, flexWrap: "wrap" }}>
            {["🔒 Bank-level security", "🍁 Made in Canada", "✓ CRA compliant"].map((badge, i) => (
              <span key={i} style={{
                fontFamily: "'DM Sans', sans-serif", fontSize: 11, color: "var(--ink-faint)",
                display: "flex", alignItems: "center", gap: 4,
              }}>{badge}</span>
            ))}
          </div>

          {/* Footer */}
          <p style={{ textAlign: "center", fontFamily: "'DM Sans', sans-serif", fontSize: 11, color: "var(--ink-faint)", marginTop: 24 }}>
            © 2026 InvoiceCloud Canada Inc.{" "}
            <button type="button" onClick={() => navigate("/")} style={{ background: "none", border: "none", color: "var(--blue)", cursor: "pointer", fontSize: 11, padding: 0 }}>
              Back to home
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}